// General MIDI instruments (all 128 programs) via sampled soundfonts.
// Samples come from the public gleitz/midi-js-soundfonts CDN, played with the
// small `soundfont-player` library (loaded lazily from jsdelivr). Three public
// soundfont banks are offered. Implements the same Instrument interface as the
// built-in Synth, so instrument tracks can use either.
import { Instrument, Synth } from "./instrument";
import { DrumKit } from "./drums";
import { SamplerInstrument } from "./sampler";
import { WamInstrument } from "./wam-instrument";
import { TrackInstrument } from "../state/model";

// GM program names (snake_case) in program order 0..127 — verified against the
// FluidR3_GM soundfont file list.
export const GM_NAMES: string[] = [
  "acoustic_grand_piano", "bright_acoustic_piano", "electric_grand_piano", "honkytonk_piano", "electric_piano_1", "electric_piano_2", "harpsichord", "clavinet",
  "celesta", "glockenspiel", "music_box", "vibraphone", "marimba", "xylophone", "tubular_bells", "dulcimer",
  "drawbar_organ", "percussive_organ", "rock_organ", "church_organ", "reed_organ", "accordion", "harmonica", "tango_accordion",
  "acoustic_guitar_nylon", "acoustic_guitar_steel", "electric_guitar_jazz", "electric_guitar_clean", "electric_guitar_muted", "overdriven_guitar", "distortion_guitar", "guitar_harmonics",
  "acoustic_bass", "electric_bass_finger", "electric_bass_pick", "fretless_bass", "slap_bass_1", "slap_bass_2", "synth_bass_1", "synth_bass_2",
  "violin", "viola", "cello", "contrabass", "tremolo_strings", "pizzicato_strings", "orchestral_harp", "timpani",
  "string_ensemble_1", "string_ensemble_2", "synth_strings_1", "synth_strings_2", "choir_aahs", "voice_oohs", "synth_choir", "orchestra_hit",
  "trumpet", "trombone", "tuba", "muted_trumpet", "french_horn", "brass_section", "synth_brass_1", "synth_brass_2",
  "soprano_sax", "alto_sax", "tenor_sax", "baritone_sax", "oboe", "english_horn", "bassoon", "clarinet",
  "piccolo", "flute", "recorder", "pan_flute", "blown_bottle", "shakuhachi", "whistle", "ocarina",
  "lead_1_square", "lead_2_sawtooth", "lead_3_calliope", "lead_4_chiff", "lead_5_charang", "lead_6_voice", "lead_7_fifths", "lead_8_bass__lead",
  "pad_1_new_age", "pad_2_warm", "pad_3_polysynth", "pad_4_choir", "pad_5_bowed", "pad_6_metallic", "pad_7_halo", "pad_8_sweep",
  "fx_1_rain", "fx_2_soundtrack", "fx_3_crystal", "fx_4_atmosphere", "fx_5_brightness", "fx_6_goblins", "fx_7_echoes", "fx_8_scifi",
  "sitar", "banjo", "shamisen", "koto", "kalimba", "bagpipe", "fiddle", "shanai",
  "tinkle_bell", "agogo", "steel_drums", "woodblock", "taiko_drum", "melodic_tom", "synth_drum", "reverse_cymbal",
  "guitar_fret_noise", "breath_noise", "seashore", "bird_tweet", "telephone_ring", "helicopter", "applause", "gunshot",
];

export const gmDisplay = (name: string) =>
  name.replace(/__/g, " ").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// 16 GM families, 8 programs each.
export const GM_FAMILIES = [
  "Piano", "Chromatic Percussion", "Organ", "Guitar", "Bass", "Strings", "Ensemble", "Brass",
  "Reed", "Pipe", "Synth Lead", "Synth Pad", "Synth Effects", "Ethnic", "Percussive", "Sound Effects",
];

// Public soundfont banks (all host the same 128 GM programs).
export const SOUNDFONTS = ["FluidR3_GM", "MusyngKite", "FatBoy"];
export const DEFAULT_SOUNDFONT = "FluidR3_GM";

let libPromise: Promise<any> | null = null;
function soundfontLib(): Promise<any> {
  if (!libPromise) {
    const url = "https://cdn.jsdelivr.net/npm/soundfont-player@0.12.0/+esm"; // variable → not resolved at build
    libPromise = import(/* @vite-ignore */ url).then((m: any) => m.default || m);
  }
  return libPromise;
}

/** An instrument backed by sampled soundfont notes (soundfont-player). `nameOrUrl`
 *  is a GM instrument name (loaded from the `soundfont` bank) or a full URL to a
 *  soundfont-player module. Samples load asynchronously (`ready`); notes before
 *  load resolves are dropped. */
export class SoundfontInstrument implements Instrument {
  output: GainNode;
  ready: Promise<void>;
  loaded = false;
  private player: any = null;
  private live = new Map<number, any>();

  constructor(private ctx: BaseAudioContext, public nameOrUrl: string, public soundfont = DEFAULT_SOUNDFONT) {
    this.output = ctx.createGain();
    this.ready = this.load();
  }

  private async load() {
    try {
      const SF = await soundfontLib();
      this.player = await SF.instrument(this.ctx, this.nameOrUrl, {
        soundfont: this.soundfont,
        destination: this.output,
      });
      this.loaded = true;
    } catch (e) { console.warn("soundfont instrument load failed", e); }
  }

  noteOn(midi: number, vel = 0.8) {
    if (!this.player) return;
    this.noteOff(midi);
    try { this.live.set(midi, this.player.play(midi, this.ctx.currentTime, { gain: vel })); } catch {}
  }
  noteOff(midi: number) {
    const n = this.live.get(midi);
    if (n) { try { n.stop((this.ctx as any).currentTime + 0.03); } catch {} this.live.delete(midi); }
  }
  scheduleNote(midi: number, vel: number, start: number, dur: number) {
    if (!this.player) return;
    try { this.player.play(midi, start, { gain: vel, duration: dur }); } catch {}
  }
  allNotesOff() {
    for (const n of this.live.values()) { try { n.stop(); } catch {} }
    this.live.clear();
    try { this.player && this.player.stop(); } catch {}
  }
  dispose() { this.allNotesOff(); try { this.output.disconnect(); } catch {} }
}

/** Build the right Instrument for a track's instrument definition. `buffers`
 *  supplies decoded AudioBuffers for the sampler (assetId → buffer). */
export function createInstrument(ctx: BaseAudioContext, def: TrackInstrument | undefined, buffers?: Map<string, AudioBuffer>): Instrument {
  if (def && def.type === "gm") return new SoundfontInstrument(ctx, GM_NAMES[def.program] || GM_NAMES[0], def.soundfont || DEFAULT_SOUNDFONT);
  if (def && def.type === "soundfont") return new SoundfontInstrument(ctx, def.url || def.name, def.soundfont || DEFAULT_SOUNDFONT);
  if (def && def.type === "drums") return new DrumKit(ctx, def.kit);
  if (def && def.type === "sampler") return new SamplerInstrument(ctx, def, buffers || new Map());
  if (def && def.type === "wam") return new WamInstrument(ctx, def.url);
  return new Synth(ctx, def && def.type === "synth" ? def.preset : "lead", def && def.type === "synth" ? def.params : undefined);
}
